type EventHandler<T> = (data: T) => void | Promise<void>;

export interface EventSubscription {
  unsubscribe: () => void;
}

type EventMap = Record<string, unknown>;

interface BusLogger {
  error(...args: unknown[]): void;
}

export interface SubscribeOptions {
  signal?: AbortSignal;
}

export class EventBus<TEvents extends EventMap> {
  private handlers = new Map<keyof TEvents, Set<EventHandler<unknown>>>();
  constructor(private logger: BusLogger = console) {}

  subscribe<K extends keyof TEvents>(
    event: K,
    handler: EventHandler<TEvents[K]>,
    options?: SubscribeOptions,
  ): EventSubscription {
    if (options?.signal?.aborted) {
      return { unsubscribe: () => {} };
    }

    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as EventHandler<unknown>);

    let abortListener: (() => void) | undefined;
    const unsubscribe = (): void => {
      const s = this.handlers.get(event);
      if (s) {
        s.delete(handler as EventHandler<unknown>);
        if (s.size === 0) this.handlers.delete(event);
      }
      if (abortListener && options?.signal) {
        options.signal.removeEventListener('abort', abortListener);
        abortListener = undefined;
      }
    };

    if (options?.signal) {
      abortListener = () => unsubscribe();
      options.signal.addEventListener('abort', abortListener, { once: true });
    }

    return { unsubscribe };
  }

  once<K extends keyof TEvents>(
    event: K,
    handler: EventHandler<TEvents[K]>,
    options?: SubscribeOptions,
  ): EventSubscription {
    const subscription = this.subscribe(
      event,
      (data) => {
        // Unsubscribe before invoking so a handler that re-publishes does not recurse.
        subscription.unsubscribe();
        return handler(data);
      },
      options,
    );
    return subscription;
  }

  publish<K extends keyof TEvents>(event: K, data: TEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        const result = (handler as EventHandler<TEvents[K]>)(data);
        if (result && typeof (result as Promise<void>).catch === 'function') {
          (result as Promise<void>).catch((err) => {
            this.logger.error(`[EventBus] async handler for "${String(event)}" threw:`, err);
          });
        }
      } catch (err) {
        this.logger.error(`[EventBus] handler for "${String(event)}" threw:`, err);
      }
    }
  }

  async publishAndWait<K extends keyof TEvents>(event: K, data: TEvents[K]): Promise<void> {
    const set = this.handlers.get(event);
    if (!set) return;
    const handlers = [...set] as EventHandler<TEvents[K]>[];
    await Promise.all(handlers.map((h) => Promise.resolve(h(data))));
  }

  async publishSequentially<K extends keyof TEvents>(
    event: K,
    data: TEvents[K],
  ): Promise<void> {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of [...set] as EventHandler<TEvents[K]>[]) {
      await handler(data);
    }
  }

  handlerCount<K extends keyof TEvents>(event: K): number {
    return this.handlers.get(event)?.size ?? 0;
  }

  clear(): void {
    this.handlers.clear();
  }
}
